import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { API, authFetch } from "@/App";
import AlbumCard from "@/components/AlbumCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, X, Loader2 } from "lucide-react";

const PAGE_SIZE = 24;

const genreOptions = [
  { name: "Rock", id: 152 },
  { name: "Pop", id: 132 },
  { name: "Hip Hop", id: 116 },
  { name: "Electronic", id: 106 },
  { name: "Jazz", id: 129 },
  { name: "R&B", id: 165 },
  { name: "Classical", id: 98 },
  { name: "Metal", id: 464 },
  { name: "Folk", id: 466 },
  { name: "Alternative", id: 85 },
  { name: "Country", id: 84 },
  { name: "Reggae", id: 144 },
  { name: "Dance", id: 113 },
];

export default function Discover() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [searchSource] = useState("deezer");
  const [mode, setMode] = useState("charts"); // "charts", "genre", "search"

  const loadMoreRef = useRef(null);
  const observerRef = useRef(null);
  const indexRef = useRef(0);

  useEffect(() => {
    const search = searchParams.get("search");
    if (search) {
      searchExternalApi(search);
    } else {
      fetchCharts(true);
    }
  }, []);

  useEffect(() => {
    const search = searchParams.get("search");
    if (search && search !== searchQuery) {
      setSearchQuery(search);
      searchExternalApi(search);
    }
  }, [searchParams]);

  const fetchCharts = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setAlbums([]);
      indexRef.current = 0;
      setHasMore(true);
      setMode("charts");
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await authFetch(
        `${API}/deezer/charts?limit=${PAGE_SIZE}&index=${indexRef.current}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length < PAGE_SIZE) setHasMore(false);
        if (reset) {
          setAlbums(data);
        } else {
          const existingIds = new Set(albums.map(a => a.album_id));
          const newAlbums = data.filter(a => !existingIds.has(a.album_id));
          if (newAlbums.length === 0) { setHasMore(false); return; }
          setAlbums(prev => [...prev, ...newAlbums]);
        }
        indexRef.current += PAGE_SIZE;
      }
    } catch (error) {
      console.error("Error fetching charts:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchGenreAlbums = async (genre, reset = false) => {
    if (reset) {
      setLoading(true);
      setAlbums([]);
      indexRef.current = 0;
      setHasMore(true);
      setMode("genre");
    } else {
      setLoadingMore(true);
    }

    try {
      const response = await authFetch(
        `${API}/deezer/genre/${genre.id}/albums?limit=${PAGE_SIZE}&index=${indexRef.current}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.length < PAGE_SIZE) setHasMore(false);
        if (reset) {
          setAlbums(data);
        } else {
          const existingIds = new Set(albums.map(a => a.album_id));
          const newAlbums = data.filter(a => !existingIds.has(a.album_id));
          if (newAlbums.length === 0) { setHasMore(false); return; }
          setAlbums(prev => [...prev, ...newAlbums]);
        }
        indexRef.current += PAGE_SIZE;
      }
    } catch (error) {
      console.error("Error fetching genre albums:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const searchExternalApi = async (query) => {
    setLoading(true);
    setMode("search");
    setHasMore(false);
    try {
      const response = await authFetch(
        `${API}/search?q=${encodeURIComponent(query)}&source=${searchSource}&limit=50`
      );
      if (response.ok) {
        const data = await response.json();
        setAlbums(data);
      }
    } catch (error) {
      console.error("Error searching albums:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    if (mode === "charts") fetchCharts(false);
    else if (mode === "genre" && selectedGenre) fetchGenreAlbums(selectedGenre, false);
  }, [loadingMore, hasMore, mode, selectedGenre, albums]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    observerRef.current = observer;

    return () => { if (observerRef.current) observerRef.current.disconnect(); };
  }, [loading, loadingMore, hasMore, loadMore]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (searchQuery.trim()) {
      params.set("search", searchQuery.trim());
      setSelectedGenre(null);
      searchExternalApi(searchQuery.trim());
    } else {
      params.delete("search");
      fetchCharts(true);
    }
    setSearchParams(params);
  };

  const handleGenreSearch = (genre) => {
    setSelectedGenre(genre);
    setSearchQuery("");
    const params = new URLSearchParams();
    params.set("genre", genre.name);
    params.set("genreId", genre.id);
    setSearchParams(params);
    fetchGenreAlbums(genre, true);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedGenre(null);
    setSearchParams({});
    fetchCharts(true);
  };

  const hasFilters = searchParams.get("search") || searchParams.get("genre");

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="discover-page">
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-purple-900/10 pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 text-white">
            Discover Albums
          </h1>
          <p className="text-white/60">
            Search millions of albums powered by Deezer.
          </p>
        </div>

        <div className="mb-8 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by album, artist, or genre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/50 border-transparent focus:border-primary rounded-full h-11"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="rounded-full px-6" data-testid="search-btn" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-white/40 mr-2">Browse by genre:</span>
            {genreOptions.map((genre) => (
              <Badge
                key={genre.id}
                variant="secondary"
                className={`cursor-pointer transition-colors ${
                  selectedGenre?.id === genre.id 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-muted/50 hover:bg-primary hover:text-primary-foreground"
                }`}
                onClick={() => handleGenreSearch(genre)}
                data-testid={`genre-${genre.name}`}
              >
                {genre.name}
              </Badge>
            ))}
          </div>

          {hasFilters && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/50">Showing results for:</span>
              <Badge variant="outline" className="gap-1 border-purple-500/50 text-white">
                {searchParams.get("search") || searchParams.get("genre")}
                <X className="w-3 h-3 cursor-pointer" onClick={clearFilters} />
              </Badge>
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-white/60 hover:text-white">
                Clear
              </Button>
            </div>
          )}
        </div>

        {!loading && (
          <p className="text-sm text-white/50 mb-6">
            {albums.length} album{albums.length !== 1 ? "s" : ""} {hasFilters ? `in ${searchParams.get("genre") || "search"}` : "(trending)"}
          </p>
        )}

        {loading ? (
          <div className="album-grid">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square rounded-lg bg-white/5 mb-3" />
                <div className="w-3/4 h-4 bg-white/5 rounded mb-2" />
                <div className="w-1/2 h-3 bg-white/5 rounded" />
              </div>
            ))}
          </div>
        ) : albums.length > 0 ? (
          <>
            <div className="album-grid stagger-children">
              {albums.map((album) => (
                <AlbumCard key={album.album_id} album={album} />
              ))}
            </div>

            {/* Infinite scroll trigger */}
            {hasMore && mode !== "search" && (
              <div ref={loadMoreRef} className="flex justify-center py-12">
                {loadingMore && (
                  <div className="flex items-center gap-3 text-white/50">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading more albums...</span>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="glass-card rounded-xl p-12 text-center">
            <Search className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-white">No albums found</h3>
            <p className="text-white/60">Try adjusting your search query.</p>
          </div>
        )}
      </div>
    </div>
  );
}
